import { useCompanyInfo, useUpdateCompany } from "@components/shared/hooks/useCompany";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_INVOICE_SEQUENTIAL_START,
  INVOICE_NUMBERING_MODES,
  type InvoiceNumberingDraft,
  type InvoiceNumberingMode,
} from "../types/invoiceNumbering.types";
import { normalizeInvoiceNumberingState, parseInvoiceStartNumber } from "../utils/normalizeInvoiceNumbering";

const toDraft = (mode: InvoiceNumberingMode, nextNumber: number | null): InvoiceNumberingDraft => ({
  invoiceNumberingMode: mode,
  invoiceStartNumber: String(nextNumber ?? DEFAULT_INVOICE_SEQUENTIAL_START),
  hasStoredNextNumber: nextNumber != null,
});

export const useInvoiceNumbering = () => {
  const { data: company, isError } = useCompanyInfo();
  const updateCompanyMutation = useUpdateCompany();
  const hydratedRef = useRef(false);

  const [isHydrated, setIsHydrated] = useState(false);
  const [saved, setSaved] = useState<InvoiceNumberingDraft>(() => toDraft(INVOICE_NUMBERING_MODES.TIMESTAMPED, null));
  const [value, setValue] = useState<InvoiceNumberingDraft>(() => toDraft(INVOICE_NUMBERING_MODES.TIMESTAMPED, null));

  useEffect(() => {
    if (hydratedRef.current) return;
    if (company === undefined && !isError) return;

    hydratedRef.current = true;

    const normalized = normalizeInvoiceNumberingState(company);
    const draft = toDraft(normalized.invoiceNumberingMode, normalized.invoiceNextNumber);

    setSaved(draft);
    setValue(draft);
    setIsHydrated(true);
  }, [company, isError]);

  const isDirty = useMemo(
    () => value.invoiceNumberingMode !== saved.invoiceNumberingMode || value.invoiceStartNumber.trim() !== saved.invoiceStartNumber.trim(),
    [saved, value],
  );

  const parsedStartNumber = parseInvoiceStartNumber(value.invoiceStartNumber);
  const requiresStartNumber =
    value.invoiceNumberingMode === INVOICE_NUMBERING_MODES.SEQUENTIAL &&
    (saved.invoiceNumberingMode !== INVOICE_NUMBERING_MODES.SEQUENTIAL || value.invoiceStartNumber.trim() !== saved.invoiceStartNumber.trim());

  const isStartNumberValid = !requiresStartNumber || parsedStartNumber != null;

  const setMode = (invoiceNumberingMode: InvoiceNumberingMode) => {
    setValue((current) => ({
      ...current,
      invoiceNumberingMode,
      invoiceStartNumber:
        invoiceNumberingMode === INVOICE_NUMBERING_MODES.SEQUENTIAL && !current.invoiceStartNumber.trim()
          ? String(DEFAULT_INVOICE_SEQUENTIAL_START)
          : current.invoiceStartNumber,
    }));
  };

  const setStartNumber = (invoiceStartNumber: string) => {
    setValue((current) => ({ ...current, invoiceStartNumber }));
  };

  const reset = () => setValue({ ...saved });

  const save = () => {
    if (!isDirty || !isStartNumberValid || updateCompanyMutation.isPending) return;

    const payload: { invoiceNumberingMode: InvoiceNumberingMode; invoiceStartNumber?: number } = {
      invoiceNumberingMode: value.invoiceNumberingMode,
    };

    if (value.invoiceNumberingMode === INVOICE_NUMBERING_MODES.SEQUENTIAL && parsedStartNumber != null) {
      const savedStart = parseInvoiceStartNumber(saved.invoiceStartNumber);
      const isResumingSequential =
        saved.invoiceNumberingMode !== INVOICE_NUMBERING_MODES.SEQUENTIAL && saved.hasStoredNextNumber && parsedStartNumber === savedStart;

      if (!isResumingSequential) {
        if (saved.invoiceNumberingMode !== INVOICE_NUMBERING_MODES.SEQUENTIAL || parsedStartNumber !== savedStart) {
          payload.invoiceStartNumber = parsedStartNumber;
        }
      }
    }

    updateCompanyMutation.mutate(payload, {
      onSuccess: () =>
        setSaved({
          ...value,
          hasStoredNextNumber: value.invoiceNumberingMode === INVOICE_NUMBERING_MODES.SEQUENTIAL || saved.hasStoredNextNumber,
        }),
    });
  };

  return {
    value,
    saved,
    isHydrated,
    isDirty,
    isStartNumberValid,
    isSaving: updateCompanyMutation.isPending,
    setMode,
    setStartNumber,
    reset,
    save,
  };
};
